import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CostOfLivingComponent } from '../dialogs/cost-of-living/cost-of-living.component';
import { InfectiousDiseaseComponent } from '../dialogs/infectious-disease/infectious-disease.component';
import { ExtendedEarningsComponent } from '../dialogs/extended-earnings/extended-earnings.component';
import { StudentLoanRepaymentComponent } from '../dialogs/student-loan-repayment/student-loan-repayment.component';
import { LiabilityPremiumComponent } from '../dialogs/liability-premium/liability-premium.component';
import { RetirementContributionComponent } from '../dialogs/retirement-contribution/retirement-contribution.component';
import { Router } from '@angular/router';
import { OpportunitySnapshotsService } from '../services/opportunity-snapshots.service';
import { SurplusLinesExistingCoverage, SurplusLinesPlanDesign, SurplusLinesPlanDesignsRequest } from '../models/surplus-lines-plan-design.model';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ConfigService } from '../services/config.service';
import { NotQualifiedComponent } from '../dialogs/not-qualified/not-qualified.component';
import { take } from 'rxjs/operators';
import { ExpiredQuoteComponent } from '../dialogs/expired-quote/expired-quote.component';
import { PaymentsService } from '../services/payments.service';

type BenefitKey = 'COLA' | 'IDR' | 'EEB' | 'SLR' | 'PLP' | 'RCB';

@Component({
  selector: 'app-quote',
  standalone: false,
  templateUrl: './quote.component.html',
  styleUrl: '../../assets/css/HighLimits.css'
})
export class QuoteComponent implements OnInit {
  monthlyBenefit: string = '';
  TTDselectedBenefit: string = '';  
  eliminationPeriod: string = '';
  annualEstimate: number = 0;

  COLA: number = 0;
  IDR: number = 0;
  EEB: number = 0;
  SLR: number = 0;
  PLP: number = 0;
  RCB: number = 0;

  COLACost: string = '';
  EEBCost: string = '';
  IDRCost: string = '';
  SLRCost: string = '';
  PLPCost: string = '';
  RCBCost: string = '';

  RCBAmount: string = '';
  RCBDuration: string = '';
  COLADuration: string = '';
  IDRDuration: string = '';
  EEBDuration: string = '';
  PLPAmount: string = '';
  PLPDuration: string = '';
  SLRAmount: string = '';
  SLRDuration: string = '';

  percent = 25;
  eliminationPeriodOptions: string[] = [];
  monthlyBenefitOptions: string[] = [];
  benefitDurationOptions: string[] = [];
  benefitDuration: string = '';
  infectiousDiseaseDuration: number = 0;
  inclusionsMet: boolean = false;

  COLAInclusion: string = '';
  IDRInclusion: string = '';
  EEBInclusion: string = '';
  SLRInclusion: string = '';
  PLPInclusion: string = '';
  RCBInclusion: string = '';

  showInclusion: boolean = false;
  notQualified: boolean = false;

  planDesignRequest: SurplusLinesPlanDesignsRequest = new SurplusLinesPlanDesignsRequest();

  snackBarConfig: MatSnackBarConfig = {
    horizontalPosition: 'center',
    verticalPosition: 'top',
    duration: 5000
  };

  signVisible: boolean = false;
  paymentVisible: boolean = false;
  questionnaireVisible: boolean = false;
   totalAnnualAmount: number = 0;

  readonly dialog = inject(MatDialog);

  private readonly BENEFITS_KEY = 'hl_addl_benefits';

  constructor(
    private _route: Router,
    private _oss: OpportunitySnapshotsService,
    private _snackbar: MatSnackBar,
    private _configService: ConfigService,
    private _paymentService: PaymentsService
  ) {}

  ngOnInit() {
    this.restoreBenefits();

    this._configService.getConfig().subscribe(() => {});

    this.COLAInclusion = this._oss.currentQuoteOptions?.COLAInclusion as string;
    this.IDRInclusion = this._oss.currentQuoteOptions?.IDRInclusion as string;
    this.EEBInclusion = this._oss.currentQuoteOptions?.EEBInclusion as string;
    this.SLRInclusion = this._oss.currentQuoteOptions?.SLRInclusion as string;
    this.PLPInclusion = this._oss.currentQuoteOptions?.PLPInclusion as string;
    this.RCBInclusion = this._oss.currentQuoteOptions?.RCBInclusion as string;

    this.setupQuote();

    const existingCoverages: SurplusLinesExistingCoverage[] = [];
    this._oss.currentOpportunitySnapshot?.OtherInsurance?.forEach(element => {
      const existing = new SurplusLinesExistingCoverage();
      existing.Amount = element.MonthlyAmount;
      existing.Type = element.Type;
      existing.Payee = element.Paid;
      existingCoverages.push(existing);
    });

    if (!this.monthlyBenefit) {
      this.monthlyBenefit = this._oss.currentQuoteOptions?.TTDMonthlyBenefit as string;
    }

    this.planDesignRequest.OpportunityId = this._oss.currentCompassOpportunity?.CompassId as number;
    this.planDesignRequest.EarnedIncome = this._oss.currentApplicant.AnnualIncome;
    this.planDesignRequest.ExistingCoverages = existingCoverages;
    this.planDesignRequest.InsuredId = this._oss.currentCompassOpportunity?.InsuredId as number;
    this.planDesignRequest.TTDMonthlyBenefit = this._oss.currentQuoteOptions?.TTDMonthlyBenefit as string; 
    this.planDesignRequest.TTDElectedBenefit = this.TTDselectedBenefit;
    this.planDesignRequest.EP = this.eliminationPeriod;
    this.planDesignRequest.COLADuration = this.COLADuration;
    this.planDesignRequest.IDRDuration = this.IDRDuration;
    this.planDesignRequest.EEBDuration = this.EEBDuration;
    this.planDesignRequest.SLRDuration = this.SLRDuration;
    this.planDesignRequest.SLRAmount = this.SLRAmount;
    this.planDesignRequest.PLPAmount = this.PLPAmount;
    this.planDesignRequest.PLPDuration = this.PLPDuration;
    this.planDesignRequest.RCBAmount = this.RCBAmount;
    this.planDesignRequest.RCBDuration = this.RCBDuration;
    this.planDesignRequest.BenefitDuration = this.benefitDuration;

    var currentApplicant = this._oss.currentApplicant;

   const quotes$ = this._oss.isReturningUser()
    ? this._oss.getExistingQuoteOptions(this._oss.currentOpportunitySnapshot.OpportunityId, this._oss.currentCompassOpportunity?.InsuredId as number, currentApplicant, this.planDesignRequest)
    : this._oss.getQuote(this._oss.currentOpportunitySnapshot.OpportunityId, this.planDesignRequest);

     quotes$.pipe(take(1))
      .subscribe({
        next: (quotes) => {
          this.getPaymentPlan();
          if (!quotes || quotes.length === 0) {
            if(this._oss.isReturningUser()){
              const dialogRef = this.dialog.open(ExpiredQuoteComponent, {
              height: '285px',
              width: '300px',
              disableClose: true,
              });
              dialogRef.updatePosition({ top: '50px' });
              dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                this._route.navigate(['/application']);
              });
              return;
            }
            else{
              const dialogRef = this.dialog.open(NotQualifiedComponent, {
                height: '285px',
                width: '300px',
                disableClose: true,
              });
              dialogRef.updatePosition({ top: '50px' });
              dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                this._route.navigate(['/landing-page']);
              });
              return;
            }
          }

          this.questionnaireVisible = this._oss.currentQuote.length > 0;
          this.paymentVisible = this._oss.currentOpportunitySnapshot.Questions.some(q => {
            const requiresAnswer = q.AnswerType == 'None'
            const hasNoBoolean = q.AnswerBoolean == null;
            const hasNoText = q.AnswerText == null || q.AnswerText.toString().trim() === '';

            return requiresAnswer || ( hasNoBoolean || hasNoText );
          });
          this.signVisible = !!(this._oss.currentOpportunitySnapshot.PaymentAccountNum || this._oss.currentOpportunitySnapshot.CreditCardPaymentMethodId);
          this.annualEstimate = quotes[0].YearOneCost;

          quotes[0].Details.forEach(detail => {
            if (detail.Attribute === null || detail.Attribute === 'Includes:')  {

              const durationOnly = this.firstParenContent(detail.Value);

              const { amount, duration } = this.parseAmountDurationSlash(detail.Value);
              if(durationOnly == "" || (amount == "0" &&  duration == "" )) {return};

              if (detail.Value.includes('COLA')) {
                this.COLA = 1;
                this.COLADuration = durationOnly || "";
              }

              if (detail.Value.includes('IDR') || detail.Value.includes('Infectious & Contagious Disease Rider')) {
                this.IDR = 1;
                this.IDRDuration = durationOnly || "";
              }

              if (detail.Value.includes('EEB') || detail.Value.includes('Extended Earnings Benefit Rider')) {
                this.EEB = 1;
                this.EEBDuration = durationOnly || "";
              }

              if (detail.Value.includes('SLR') || detail.Value.includes('Student Loan Repayment Rider')) {
                this.SLR = 1;
                this.planDesignRequest.SLRAmount = this.SLRAmount = amount || "";
                this.SLRDuration = duration || "";
              }

              if (detail.Value.includes('PLP') || detail.Value.includes('Professional Liability Premium Rider')) {
                this.PLP = 1;
                this.planDesignRequest.PLPAmount = this.PLPAmount = amount || "";
                this.PLPDuration = duration || "";
              }

              if (detail.Value.includes('RCB') || detail.Value.includes('Retirement Contribution Benefit')) {
                this.RCB = 1;
                this.planDesignRequest.RCBAmount = this.RCBAmount = amount || "";
                this.RCBDuration = duration || "";
              }
            }
            if (detail.Attribute === null || detail.Attribute === 'Monthly Benefit')  { 
              this.TTDselectedBenefit = detail.Value.replace(/[^0-9.]+/g, "");
            }
            if (detail.Attribute === null || detail.Attribute === 'Elimination Period:')  { 
              this.eliminationPeriod = detail.Value;
            }
          });

          this.monthlyBenefitOptions = this.getMonthlyBenefitOptions();
          if(!this.TTDselectedBenefit || this.TTDselectedBenefit == '') {
            this.TTDselectedBenefit = String(this._oss.currentQuote[0].TTDMaxBenefit);
          }
          this.persistBenefits();
        },
        error: (err) => {
          console.error('Error getting quotes', err);
        }
      });
  }

  public firstParenContent(s: string): string {
    const m = s.match(/\(([^()]*)\)/);
    return m ? m[1].trim() : "";
  }

  public parseAmountDurationSlash(s: string): { amount: string; duration: string } {
  const inside = this.firstParenContent(s);
  if (!inside) return { amount: "", duration: "" };

  const parts = inside.split(/\s*\/\s*/);
  if (parts.length < 2) return { amount: "", duration: "" };

  const rawAmount = parts[0] ?? "";
  const rawDuration = parts[1] ?? "";

  const amount = rawAmount.replace(/\D/g, "");
  const duration = rawDuration.trim();

  return { amount, duration };
}

  private persistBenefits(): void {
    try {
      sessionStorage.setItem(this.BENEFITS_KEY, JSON.stringify({
        COLA: this.COLA, COLADuration: this.COLADuration, COLACost: this.COLACost,
        IDR: this.IDR, IDRDuration: this.IDRDuration, IDRCost: this.IDRCost,
        EEB: this.EEB, EEBDuration: this.EEBDuration, EEBCost: this.EEBCost,
        SLR: this.SLR, SLRDuration: this.SLRDuration, SLRAmount: this.SLRAmount, SLRCost: this.SLRCost,
        PLP: this.PLP, PLPDuration: this.PLPDuration, PLPAmount: this.PLPAmount, PLPCost: this.PLPCost,
        RCB: this.RCB, RCBDuration: this.RCBDuration, RCBAmount: this.RCBAmount, RCBCost: this.RCBCost,
        eliminationPeriod: this.eliminationPeriod,
        monthlyBenefit: this.monthlyBenefit,
        TTDselectedBenefit: this.TTDselectedBenefit,
        benefitDuration: this.benefitDuration
      }));
    } catch {}
  }

  private restoreBenefits(): void {
    const saved = sessionStorage.getItem(this.BENEFITS_KEY);
    if (!saved) return;
    try {
      Object.assign(this, JSON.parse(saved));
    } catch {}
  }

  hasRequiredInclusions(): boolean {
    if (this._oss.currentQuoteOptions?.COLAInclusion === 'Y' && this.COLA !== 1) return false;
    if (this._oss.currentQuoteOptions?.EEBInclusion  === 'Y' && this.EEB  !== 1) return false;
    if (this._oss.currentQuoteOptions?.IDRInclusion  === 'Y' && this.IDR  !== 1) return false;
    if (this._oss.currentQuoteOptions?.PLPInclusion  === 'Y' && this.PLP  !== 1) return false;
    if (this._oss.currentQuoteOptions?.RCBInclusion  === 'Y' && this.RCB  !== 1) return false;
    if (this._oss.currentQuoteOptions?.SLRInclusion  === 'Y' && this.SLR  !== 1) return false;
    return true;
  }

  canApply(): boolean { return this.hasRequiredInclusions(); }

  getEliminationPeriodOptions(): string[] {
    if (!this._oss.currentQuoteOptions) return [];
    return this._oss.currentQuoteOptions.EPOptions.replaceAll('; ', ';').split(';');
  }

  getBenefitDurationOptions(): string[] {
    if (!this._oss.currentQuoteOptions) return [];
    return this._oss.currentQuoteOptions.DurationOptions.replaceAll('; ', ';').split(';');
  }

  getMonthlyBenefitOptions(): string[] {
    if (!this._oss.currentQuoteOptions) return [];
    const max = Number(this._oss.currentQuote[0].TTDMaxBenefit);
    const increment = Number(this._oss.currentQuoteOptions?.Increment);
    const options: number[] = [];
    for (let i = 1000; i < max; i += increment) options.push(i);
    if (!options.includes(max)) options.push(max);
    return options.map(x => x.toString());
  }

  setupQuote() {
    this.eliminationPeriodOptions = this.getEliminationPeriodOptions();
    this.benefitDurationOptions = this.getBenefitDurationOptions();

    if (!this.eliminationPeriod) this.eliminationPeriod = this._oss.currentQuoteOptions?.EPDefault as string;
    if (!this.benefitDuration)  this.benefitDuration  = this._oss.currentQuoteOptions?.DurationDefault as string;

    if (this.COLAInclusion === 'Y') {
      this.COLA = 1;
      if (!this.COLADuration) this.COLADuration = this._oss.currentQuoteOptions?.COLADurationDefault as string;
    } else {
      this.COLA = 0; this.COLADuration = ''; this.COLACost = '';
    }

    if (this.IDRInclusion === 'Y') {
      this.IDR = 1;
      if (!this.IDRDuration) this.IDRDuration = this._oss.currentQuoteOptions?.IDRDurationDefault as string;
    } else {
      this.IDR = 0; this.IDRDuration = ''; this.IDRCost = '';
    }

    if (this.EEBInclusion === 'Y') {
      this.EEB = 1;
      if (!this.EEBDuration) this.EEBDuration = this._oss.currentQuoteOptions?.EEBDurationDefault as string;
    } else {
      this.EEB = 0; this.EEBDuration = ''; this.EEBCost = '';
    }

    if (this.SLRInclusion === 'Y') {
      this.SLR = 1;
      if (!this.SLRDuration) this.SLRDuration = this._oss.currentQuoteOptions?.SLRDurationDefault as string;
      if (!this.SLRAmount)   this.SLRAmount   = this._oss.currentQuoteOptions?.SLRAmountDefault   as string;
    } else {
      this.SLR = 0; this.SLRDuration = ''; this.SLRAmount = ''; this.SLRCost = '';
    }

    if (this.PLPInclusion === 'Y') {
      this.PLP = 1;
      if (!this.PLPDuration) this.PLPDuration = this._oss.currentQuoteOptions?.PLPDurationDefault as string;
      if (!this.PLPAmount)   this.PLPAmount   = this._oss.currentQuoteOptions?.PLPAmountDefault   as string;
    } else {
      this.PLP = 0; this.PLPDuration = ''; this.PLPAmount = ''; this.PLPCost = '';
    }

    if (this.RCBInclusion === 'Y') {
      this.RCB = 1;
      if (!this.RCBDuration) this.RCBDuration = this._oss.currentQuoteOptions?.RCBDurationDefault as string;
      if (!this.RCBAmount)   this.RCBAmount   = this._oss.currentQuoteOptions?.RCBAmountDefault   as string;
    } else {
      this.RCB = 0; this.RCBDuration = ''; this.RCBAmount = ''; this.RCBCost = '';
    }
  }

  applyNow() {
    if (this.canApply()) {
      this.persistBenefits();
      this.gotoQuestionnaire();
    } else {
      this.showInclusion = true;
      this._snackbar.open('Additional benefits are required to apply', 'Close', this.snackBarConfig);
    }
  }

  refreshPlan(preAdditionalBenefit?: BenefitKey) {

    const existingCoverages: SurplusLinesExistingCoverage[] = [];
    this._oss.currentOpportunitySnapshot?.OtherInsurance?.forEach(element => {
      const existing = new SurplusLinesExistingCoverage();
      existing.Amount = element.MonthlyAmount;
      existing.Type = element.Type;
      existing.Payee = element.Paid;
      existingCoverages.push(existing);
    });

    this.planDesignRequest.OpportunityId = this._oss.currentCompassOpportunity?.CompassId as number;
    this.planDesignRequest.EarnedIncome = this._oss.currentApplicant.AnnualIncome;
    this.planDesignRequest.ExistingCoverages = existingCoverages;
    this.planDesignRequest.InsuredId = this._oss.currentCompassOpportunity?.InsuredId as number;
    this.planDesignRequest.TTDMonthlyBenefit = this._oss.currentQuoteOptions?.TTDMonthlyBenefit as string;
    this.planDesignRequest.TTDElectedBenefit = this.TTDselectedBenefit;
    this.planDesignRequest.EP = this.eliminationPeriod;
    this.planDesignRequest.BenefitDuration = this.benefitDuration;

    this.planDesignRequest.COLADuration = this.COLA ? this.COLADuration : '';
    this.planDesignRequest.IDRDuration = this.IDR ? this.IDRDuration : '';
    this.planDesignRequest.EEBDuration = this.EEB ? this.EEBDuration : '';
    this.planDesignRequest.SLRDuration = this.SLR ? this.SLRDuration : '';
    this.planDesignRequest.SLRAmount   = this.SLR ? this.SLRAmount   : '';
    this.planDesignRequest.PLPAmount   = this.PLP ? this.PLPAmount   : '';
    this.planDesignRequest.PLPDuration = this.PLP ? this.PLPDuration : '';
    this.planDesignRequest.RCBAmount   = this.RCB ? this.RCBAmount   : '';
    this.planDesignRequest.RCBDuration = this.RCB ? this.RCBDuration : '';

    if (this._oss.currentQuoteOptions && preAdditionalBenefit) {
        switch (preAdditionalBenefit) {
      case 'COLA':
        this.planDesignRequest.COLADuration ??= this._oss.currentQuoteOptions?.COLADurationDefault ?? "";
        break;
      case 'IDR':
        this.planDesignRequest.IDRDuration ??= this._oss.currentQuoteOptions?.IDRDurationDefault ?? "";
        break;
      case 'EEB':
        this.planDesignRequest.EEBDuration ??= this._oss.currentQuoteOptions?.EEBDurationDefault ?? "";
        break;
      case 'SLR':
        this.planDesignRequest.SLRDuration ??= this._oss.currentQuoteOptions?.SLRDurationDefault ?? "";
        this.planDesignRequest.SLRAmount   ??= this._oss.currentQuoteOptions?.SLRAmountDefault   ?? "";
        break;
      case 'PLP':
        this.planDesignRequest.PLPDuration ??= this._oss.currentQuoteOptions?.PLPDurationDefault ?? "";
        this.planDesignRequest.PLPAmount   ??= this._oss.currentQuoteOptions?.PLPAmountDefault   ?? "";
        break;
      case 'RCB':
        this.planDesignRequest.RCBDuration ??= this._oss.currentQuoteOptions?.RCBDurationDefault ?? "";
        this.planDesignRequest.RCBAmount   ??= this._oss.currentQuoteOptions?.RCBAmountDefault   ?? "";
        break;
      }
    }

    this._oss.getQuote(this._oss.currentOpportunitySnapshot.OpportunityId, this.planDesignRequest)
      .pipe(take(1))
      .subscribe(quotes => {
        if (quotes && quotes.length > 0) {
          this.getPaymentPlan();
          this.annualEstimate = quotes[0].YearOneCost;
        }
        this.persistBenefits();
      });
  }

  designValueUpdate(Request: string, Default: string) {
    return Request != null ? Request : Default;
  }

  onCOLAChange(checked: boolean) {
    if (checked) {
      const dialogRef = this.dialog.open(CostOfLivingComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.COLADuration = this.COLADuration;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.COLA = 1;
          this.COLADuration = result.COLADuration;
          this.COLACost = result.cost;
        } else {
          this.COLA = 0;
          this.COLADuration = '';
          this.COLACost = '';
        }
        this.refreshPlan('COLA');
        this.persistBenefits();
      });
    } else {
      this.COLA = 0; this.COLADuration = ''; this.COLACost = '';
      this.refreshPlan('COLA');
      this.persistBenefits();
    }
  }

  onIDRChange(checked: boolean) {
    if (checked) {
      const dialogRef = this.dialog.open(InfectiousDiseaseComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.IDRDuration = this.IDRDuration;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.IDR = 1;
          this.IDRDuration = result.IDRDuration;
          this.IDRCost = result.cost;
        } else {
          this.IDR = 0;
          this.IDRDuration = '';
          this.IDRCost = '';
        }
        this.refreshPlan('IDR');
        this.persistBenefits();
      });
    } else {
      this.IDR = 0; this.IDRDuration = ''; this.IDRCost = '';
      this.refreshPlan('IDR');
      this.persistBenefits();
    }
  }

  onEEBChange(checked: boolean) {
    if (checked) {
      const dialogRef = this.dialog.open(ExtendedEarningsComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.EEB = 1;
          this.EEBDuration = result.EEBDuration;
          this.EEBCost = result.cost;
        } else {
          this.EEB = 0;
          this.EEBDuration = '';
          this.EEBCost = '';
        }
        this.refreshPlan('EEB');
        this.persistBenefits();
      });
    } else {
      this.EEB = 0; this.EEBDuration = ''; this.EEBCost = '';
      this.refreshPlan('EEB');
      this.persistBenefits();
    }
  }

  onSLRChange(checked: boolean) {
    if (checked) {
      const dialogRef = this.dialog.open(StudentLoanRepaymentComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.SLR = 1;
          this.SLRAmount = result.SLRAmount;
          this.SLRDuration = result.SLRDuration;
          this.SLRCost = result.cost;
        } else {
          this.SLR = 0;
          this.SLRAmount = '';
          this.SLRDuration = '';
          this.SLRCost = '';
        }
        this.refreshPlan('SLR');
        this.persistBenefits();
      });
    } else {
      this.SLR = 0; this.SLRAmount = ''; this.SLRDuration = ''; this.SLRCost = '';
      this.refreshPlan('SLR');
      this.persistBenefits();
    }
  }

  onPLPChange(checked: boolean) {
    if (checked) {
      if (this.PLPInclusion === 'Y') {
        this.PLP = 1;
        this.PLPDuration = this._oss.currentQuoteOptions?.PLPDurationDefault as string;
        this.PLPAmount   = this._oss.currentQuoteOptions?.PLPAmountDefault   as string;
        this.refreshPlan('PLP');
        this.persistBenefits();
        return;
      }

      const dialogRef = this.dialog.open(LiabilityPremiumComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.PLP = 1;
          this.PLPAmount = result.PLPAmount;
          this.PLPDuration = result.PLPDuration;
          this.PLPCost = result.cost;
        } else {
          this.PLP = 0;
          this.PLPAmount = '';
          this.PLPDuration = '';
          this.PLPCost = '';
        }
        this.refreshPlan('PLP');
        this.persistBenefits();
      });
    } else {
      this.PLP = 0; this.PLPAmount = ''; this.PLPDuration = ''; this.PLPCost = '';
      this.refreshPlan('PLP');
      this.persistBenefits();
    }
  }

  onRCBChange(checked: boolean) {
    if (checked) {
      const dialogRef = this.dialog.open(RetirementContributionComponent, {
        width: '400px', disableClose: true, autoFocus: false,
      });
      const instance = dialogRef.componentInstance;
      instance.monthlyBenefit = this.monthlyBenefit;
      instance.eliminationPeriod = this.eliminationPeriod;
      instance.planDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);
      instance.additionalPlanDesignRequest = Object.assign(Object.create(Object.getPrototypeOf(this.planDesignRequest)), this.planDesignRequest);

      dialogRef.updatePosition({ top: '50px' });
      dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
        if (result) {
          this.RCB = 1;
          this.RCBAmount = result.RCBAmount;
          this.RCBDuration = result.RCBDuration;
          this.RCBCost = result.cost;
        } else {
          this.RCB = 0;
          this.RCBAmount = '';
          this.RCBDuration = '';
          this.RCBCost = '';
        }
        this.refreshPlan('RCB');
        this.persistBenefits();
      });
    } else {
      this.RCB = 0; this.RCBAmount = ''; this.RCBDuration = ''; this.RCBCost = '';
      this.refreshPlan('RCB');
      this.persistBenefits();
    }
  }

  gotoPay() { this.persistBenefits(); this._route.navigate(['/payment']); }
  gotoSign() { this.persistBenefits(); this._route.navigate(['/sign']); }
  gotoQuestionnaire() { this.persistBenefits(); this._route.navigate(['/questionnaire']); }
  getPaymentPlan(){
     this._paymentService.getPaymentPlan(this._oss.currentOpportunitySnapshot.OpportunityId).subscribe((response: any) => {
       this.totalAnnualAmount = Number(response.TotalCost) || this._oss.currentQuote[0].YearOneCost as number;
       if(response.TotalCost != null){
         this._paymentService.setAnnualPayment(this.totalAnnualAmount);
       }
     });
  }
}